/**
 * AudioManager — high-fidelity spatial audio for Dard-e-Disco.
 *   - Music (record player on East wall): routed through Web Audio MediaElementSource →
 *     vinyl lowpass filter → gain → StereoPannerNode → masterGain.
 *   - Ambient (window on West wall): synthesized pink-noise traffic drone + FM bird chirps →
 *     gain → StereoPannerNode → masterGain.
 *
 * Local high-quality tracks provide zero-CORS Web Audio spatial routing with
 * deterministic left/right stereo positioning and distance/gaze attenuation.
 */

const publicUrl = process.env.PUBLIC_URL || "";
export const TRACKS = [
  {
    id: "dard-e-disco",
    title: "Dard-e-Disco",
    badge: "FEATURED",
    url: `${publicUrl}/tracks/neon-pulse-dard-e-disco.mp3`,
  },
  { id: "night-loop", title: "Night Loop", artist: "SoundHelix 08", url: `${publicUrl}/tracks/track1.mp3` },
  { id: "side-b",     title: "Side B",     artist: "SoundHelix 03", url: `${publicUrl}/tracks/track2.mp3` },
  { id: "opener",     title: "Opener",     artist: "SoundHelix 01", url: `${publicUrl}/tracks/track3.mp3` },
  { id: "signal",     title: "Signal",     artist: "SoundHelix 05", url: `${publicUrl}/tracks/track4.mp3` },
];

export class AudioManager {
  constructor({ onTrackChange } = {}) {
    this.ctx = null;
    this.masterGain = null;

    this.musicEl = null;
    this.musicGain = null;
    this.musicPanner = null;
    this.musicSpatial = false;
    this.currentTrackIndex = -1;
    this.onTrackChange = onTrackChange || (() => {});
    this._onEnded = null;

    this.ambientGain = null;
    this.ambientPanner = null;
    this.birdTimer = null;

    this._musicVolume = 0.7;
    this._ambientVolume = 0.4;

    this.surround = false;
    this.vinylFilter = null;

    this.started = false;
  }

  async setTrack(index) {
    if (index < 0 || index >= TRACKS.length) return;
    if (index === this.currentTrackIndex && this.musicEl && !this.musicEl.ended) return;

    const prevVol = this.musicSpatial && this.musicGain ? this.musicGain.gain.value : (this.musicEl ? this.musicEl.volume : 0.7);
    const isEnded = this.musicEl ? this.musicEl.ended : false;

    // Fast smooth fade-out (only if currently playing and hasn't already ended)
    if (this.musicSpatial && this.musicGain && this.ctx && !isEnded) {
      const t = this.ctx.currentTime;
      this.musicGain.gain.setValueAtTime(prevVol, t);
      this.musicGain.gain.linearRampToValueAtTime(0, t + 0.15);
      await new Promise((r) => setTimeout(r, 160));
    }

    if (this.musicEl) {
      this.musicEl.pause();
    }

    this.currentTrackIndex = index;
    const targetTrack = TRACKS[index];

    if (this.musicEl && targetTrack.url) {
      this.musicEl.src = targetTrack.url;
      this.musicEl.currentTime = 0;
      try {
        await this.musicEl.play();
      } catch (e) {
        console.warn("[audio] track switch play failed", e);
      }
    }

    // Fast smooth fade-in
    if (this.musicSpatial && this.musicGain && this.ctx) {
      const t = this.ctx.currentTime;
      this.musicGain.gain.linearRampToValueAtTime(this._musicVolume, t + 0.2);
    } else if (this.musicEl) {
      this.musicEl.volume = this._musicVolume;
    }

    if (this.onTrackChange) {
      this.onTrackChange(index);
    }
  }

  async nextTrack() {
    const nextIndex = (this.currentTrackIndex + 1) % TRACKS.length;
    await this.setTrack(nextIndex);
  }

  async start(initialIndex) {
    if (this.started) return;
    this.started = true;

    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    this.ctx = new AudioCtx();
    if (this.ctx.state === "suspended") await this.ctx.resume();

    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = 1.0;
    this.masterGain.connect(this.ctx.destination);

    // -------- MUSIC (Record Player) --------
    this.musicEl = new Audio();
    this.musicEl.crossOrigin = "anonymous";
    this.musicEl.loop = false; // Never loop single track; advance to next track on ended
    this.musicEl.preload = "auto";

    this._onEnded = () => {
      this.nextTrack();
    };
    this.musicEl.addEventListener("ended", this._onEnded);

    // Choose initial track: random if not explicitly provided
    const startIndex = (typeof initialIndex === "number" && initialIndex >= 0 && initialIndex < TRACKS.length)
      ? initialIndex
      : Math.floor(Math.random() * TRACKS.length);
    this.currentTrackIndex = startIndex;

    try {
      const src = this.ctx.createMediaElementSource(this.musicEl);
      this.musicGain = this.ctx.createGain();
      this.musicGain.gain.value = this._musicVolume;
      this.musicPanner = this.ctx.createStereoPanner ? this.ctx.createStereoPanner() : null;

      // Warm vinyl lowpass (subtle retro warmth in spatial mode, full range in surround mode)
      this.vinylFilter = this.ctx.createBiquadFilter();
      this.vinylFilter.type = "lowpass";
      this.vinylFilter.frequency.value = this.surround ? 18000 : 6500;

      if (this.musicPanner) {
        src.connect(this.vinylFilter).connect(this.musicGain).connect(this.musicPanner).connect(this.masterGain);
      } else {
        src.connect(this.vinylFilter).connect(this.musicGain).connect(this.masterGain);
      }
      this.musicSpatial = true;
    } catch (e) {
      console.warn("[audio] Web Audio routing fallback:", e);
      this.musicSpatial = false;
      this.musicEl.volume = this._musicVolume;
    }

    // Start the chosen track
    const initialTrack = TRACKS[startIndex];
    if (this.musicEl && initialTrack.url) {
      this.musicEl.src = initialTrack.url;
      try {
        await this.musicEl.play();
      } catch (e) {
        this.ctx.resume().then(() => this.musicEl?.play().catch(() => {}));
      }
    }

    if (this.onTrackChange) {
      this.onTrackChange(startIndex);
    }

    // -------- AMBIENT (Window) --------
    this.ambientGain = this.ctx.createGain();
    this.ambientGain.gain.value = this._ambientVolume;
    this.ambientPanner = this.ctx.createStereoPanner ? this.ctx.createStereoPanner() : null;


    if (this.ambientPanner) {
      this.ambientGain.connect(this.ambientPanner).connect(this.masterGain);
    } else {
      this.ambientGain.connect(this.masterGain);
    }

    // Pink-noise traffic drone
    const bufferSize = 2 * this.ctx.sampleRate;
    const noiseBuffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const data = noiseBuffer.getChannelData(0);
    let b0 = 0, b1 = 0, b2 = 0;
    for (let i = 0; i < bufferSize; i++) {
      const white = Math.random() * 2 - 1;
      b0 = 0.99765 * b0 + white * 0.099046;
      b1 = 0.96300 * b1 + white * 0.2965164;
      b2 = 0.57000 * b2 + white * 1.0526913;
      data[i] = (b0 + b1 + b2 + white * 0.1848) * 0.13;
    }
    const noise = this.ctx.createBufferSource();
    noise.buffer = noiseBuffer;
    noise.loop = true;

    const trafficLP = this.ctx.createBiquadFilter();
    trafficLP.type = "lowpass";
    trafficLP.frequency.value = 650;
    trafficLP.Q.value = 0.6;

    const trafficHP = this.ctx.createBiquadFilter();
    trafficHP.type = "highpass";
    trafficHP.frequency.value = 60;

    noise.connect(trafficHP).connect(trafficLP).connect(this.ambientGain);
    noise.start();

    // Slow LFO for passing traffic swells
    const lfo = this.ctx.createOscillator();
    lfo.frequency.value = 0.08;
    const lfoGain = this.ctx.createGain();
    lfoGain.gain.value = 220;
    lfo.connect(lfoGain).connect(trafficLP.frequency);
    lfo.start();

    this._scheduleBirds();
  }

  _scheduleBirds() {
    const chirp = () => {
      if (!this.ctx || !this.ambientGain) return;
      const t = this.ctx.currentTime;
      const notes = 1 + Math.floor(Math.random() * 3);
      for (let n = 0; n < notes; n++) {
        const start = t + n * 0.08 + Math.random() * 0.05;
        const osc = this.ctx.createOscillator();
        const mod = this.ctx.createOscillator();
        const modGain = this.ctx.createGain();
        const g = this.ctx.createGain();

        const base = 1800 + Math.random() * 2400;
        osc.frequency.setValueAtTime(base, start);
        osc.frequency.exponentialRampToValueAtTime(base * (0.8 + Math.random() * 0.7), start + 0.15);

        mod.frequency.value = 22 + Math.random() * 40;
        modGain.gain.value = 120 + Math.random() * 200;
        mod.connect(modGain).connect(osc.frequency);

        g.gain.setValueAtTime(0, start);
        g.gain.linearRampToValueAtTime(0.055, start + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, start + 0.18);

        osc.connect(g).connect(this.ambientGain);
        osc.start(start);
        mod.start(start);
        osc.stop(start + 0.22);
        mod.stop(start + 0.22);
      }
      this.birdTimer = setTimeout(chirp, 1500 + Math.random() * 4500);
    };
    chirp();
  }

  /**
   * Compatibility listener update (maintains API consistency).
   */
  updateListener(pos, forward, up) {
    if (!this.ctx) return;
    const l = this.ctx.listener;
    const t = this.ctx.currentTime;
    if (l.positionX) {
      l.positionX.setValueAtTime(pos.x, t);
      l.positionY.setValueAtTime(pos.y, t);
      l.positionZ.setValueAtTime(pos.z, t);
      l.forwardX.setValueAtTime(forward.x, t);
      l.forwardY.setValueAtTime(forward.y, t);
      l.forwardZ.setValueAtTime(forward.z, t);
      l.upX.setValueAtTime(up.x, t);
      l.upY.setValueAtTime(up.y, t);
      l.upZ.setValueAtTime(up.z, t);
    } else if (l.setPosition) {
      l.setPosition(pos.x, pos.y, pos.z);
      l.setOrientation(forward.x, forward.y, forward.z, up.x, up.y, up.z);
    }
  }

  /** User master volume sliders (0..1) */
  setMusicVolume(v) {
    this._musicVolume = Math.max(0, Math.min(1, v));
  }

  setAmbientVolume(v) {
    this._ambientVolume = Math.max(0, Math.min(1, v));
  }

  /**
   * Toggle surround sound for the record player.
   * - Surround ON: Full room immersion, crystal-clear studio highs (18kHz), omni-directional.
   * - Surround OFF: Physically localized East wall audio (6.5kHz warm vinyl acoustic, spatial pan & distance).
   */
  setSurround(enabled) {
    this.surround = !!enabled;
    if (this.vinylFilter && this.ctx) {
      const targetFreq = this.surround ? 18000 : 6500;
      this.vinylFilter.frequency.setTargetAtTime(targetFreq, this.ctx.currentTime, 0.05);
    }
  }

  /**
   * Instantaneous spatial level & stereo pan modulation.
   * Called every frame with exact distance gain, gaze focus, and stereo pan.
   */
  setLevels(arg1, arg2) {
    let music = 0.5, ambient = 0.5, panMusic = 0, panAmbient = 0;
    if (typeof arg1 === "object" && arg1 !== null) {
      music = arg1.music ?? 0.5;
      ambient = arg1.ambient ?? 0.5;
      panMusic = arg1.panMusic ?? 0;
      panAmbient = arg1.panAmbient ?? 0;
    } else {
      music = arg1 ?? 0.5;
      ambient = arg2 ?? 0.5;
    }

    const targetMusic = Math.max(0, Math.min(1, music * this._musicVolume));
    const targetAmbient = Math.max(0, Math.min(1, ambient * this._ambientVolume));

    // Near-zero time constant (0.02s) gives immediate, lag-free response without audio clicks
    if (this.musicSpatial && this.musicGain && this.ctx) {
      this.musicGain.gain.setTargetAtTime(targetMusic, this.ctx.currentTime, 0.02);
    } else if (this.musicEl) {
      this.musicEl.volume = targetMusic;
    }

    if (this.musicPanner && this.ctx) {
      this.musicPanner.pan.setTargetAtTime(Math.max(-1, Math.min(1, panMusic)), this.ctx.currentTime, 0.02);
    }

    if (this.ambientGain && this.ctx) {
      this.ambientGain.gain.setTargetAtTime(targetAmbient, this.ctx.currentTime, 0.02);
    }

    if (this.ambientPanner && this.ctx) {
      this.ambientPanner.pan.setTargetAtTime(Math.max(-1, Math.min(1, panAmbient)), this.ctx.currentTime, 0.02);
    }
  }

  stop() {
    if (this.birdTimer) clearTimeout(this.birdTimer);
    if (this.musicEl) {
      if (this._onEnded) this.musicEl.removeEventListener("ended", this._onEnded);
      this.musicEl.pause();
      this.musicEl.src = "";
    }
    if (this.ctx) this.ctx.close();
    this.started = false;
  }
}
